// Utility functions for formatting

export const formatCurrency = (amount) => {
  if (!amount) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

/**
 * Sanitize free-text input for a money/decimal field.
 *
 * Used with <input type="text" inputMode="decimal"> instead of
 * <input type="number">. Number inputs re-normalize their value on every
 * keystroke, which makes the caret jump to the end when you edit in the
 * middle of a pre-filled value like "1500.00".
 *
 * Accepts: digits, at most one decimal point, optional leading/trailing
 * partial states while typing ("", ".", "12.", "0.5").
 * Rejects (returns null, caller ignores the keystroke): letters, symbols,
 * a second decimal point.
 *
 * Returns the cleaned string to store, or null if the input should be
 * rejected outright (leaving state — and therefore the caret — untouched).
 */
export const sanitizeDecimalInput = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return '';
  const str = String(raw);
  // Allow only digits and dots; anything else is a rejected keystroke.
  if (!/^[0-9.]*$/.test(str)) return null;
  // At most one decimal point.
  if ((str.match(/\./g) || []).length > 1) return null;
  return str;
};

/**
 * A money value on its way into a box someone reads, edits and sends on.
 *
 * A fee computed by division arrives with a tail — $50,000 / 3 is
 * 16666.666666666668 — and a sheet saved before that was rounded at the
 * source still carries one. Returns a string, because these boxes hold
 * strings so a half-typed figure is not read as a number mid-keystroke.
 *
 * Blank stays blank: an empty fee is not a zero fee, and turning it into one
 * would quietly put a figure on a sheet nobody entered.
 */
export const trimToCents = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = Number(value);
  return Number.isFinite(num) ? String(Math.round(num * 100) / 100) : String(value);
};

/**
 * Format a DATE-ONLY value without timezone drift.
 *
 * THE BUG THIS FIXES: Filevine returns dates like "2026-02-03" with no
 * time component. `new Date("2026-02-03")` is specified to parse as UTC
 * midnight. Calling .toLocaleDateString() then renders it in the browser's
 * local zone — in Pacific (UTC-7/-8) that's 4pm or 5pm the PREVIOUS day,
 * so a DOL of Feb 3 displays as Feb 2.
 *
 * Fix: pull the Y/M/D components straight out of the string and build a
 * local-time Date, so no UTC round-trip happens.
 *
 * Handles:
 *   "2026-02-03"                  -> 2/3/2026   (date-only, the buggy case)
 *   "2026-02-03T00:00:00"         -> 2/3/2026   (naive datetime, no zone)
 *   "2026-02-03T18:30:00Z"        -> falls through to normal Date parsing
 *                                    (real timestamp — zone conversion is
 *                                     correct and wanted here)
 *   null / "" / undefined         -> fallback string
 */
export const formatDateOnly = (value, fallback = 'N/A') => {
  if (!value) return fallback;

  // Already a Date object — render as-is.
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? fallback : value.toLocaleDateString();
  }

  const str = String(value).trim();
  if (!str) return fallback;

  // Match a leading YYYY-MM-DD. If the remainder is empty or a zone-less
  // time, treat it as a wall-clock date and build it in LOCAL time.
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](.*))?$/);
  if (m) {
    const [, y, mo, d, rest] = m;
    const hasZone = rest && /(Z|[+-]\d{2}:?\d{2})$/i.test(rest.trim());
    if (!hasZone) {
      // Local-time construction — month is 0-indexed.
      const dt = new Date(Number(y), Number(mo) - 1, Number(d));
      return isNaN(dt.getTime()) ? fallback : dt.toLocaleDateString();
    }
  }

  // Real timestamp with a zone, or some other format — normal parsing is
  // correct here (we WANT zone conversion for actual moments in time).
  const dt = new Date(str);
  return isNaN(dt.getTime()) ? fallback : dt.toLocaleDateString();
};

/**
 * Format an audit timestamp in the firm's timezone.
 *
 * Two problems this solves, both of which produced visibly wrong times:
 *
 * 1. The backend stores datetime.utcnow() — naive, with no zone marker. An
 *    ISO string without one is parsed by JavaScript as LOCAL time, so a UTC
 *    timestamp was being displayed as though it were already local. Events
 *    appeared seven or eight hours off: an email sent at 7:17 PM Pacific
 *    showed as 2:17 AM the next day.
 *
 * 2. Bare toLocaleString() renders in the VIEWER's zone, so the same event
 *    read differently depending on whose laptop was open. Providers are
 *    asked to find an email by the time we quote them, so the time has to be
 *    the firm's, consistently, and labelled.
 *
 * Contrast with formatDateOnly, where a zone-less value is a wall-clock date
 * and must NOT be converted.
 */
/**
 * Parse a timestamp from this backend.
 *
 * The API stores datetime.utcnow() — naive, with no zone marker — and
 * JavaScript reads a marker-less ISO string as LOCAL time. Anything that
 * parses one of these with a bare `new Date(...)` is therefore off by the
 * viewer's UTC offset. Use this instead.
 *
 * Returns a Date, or null when the value is empty or unparseable.
 */
export const parseServerDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const str = String(value).trim();
  if (!str) return null;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(str);
  const dt = new Date(hasZone ? str : `${str.replace(' ', 'T')}Z`);
  return isNaN(dt.getTime()) ? null : dt;
};

export const FIRM_TIMEZONE = 'America/Los_Angeles';

export const formatDateTime = (value, fallback = 'N/A') => {
  if (!value) return fallback;

  const dt = parseServerDate(value);
  if (!dt) return fallback;

  const formatted = dt.toLocaleString('en-US', {
    timeZone: FIRM_TIMEZONE,
    month: 'numeric', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
  // Label it, so nobody has to guess whose clock this is.
  const abbr = new Intl.DateTimeFormat('en-US', {
    timeZone: FIRM_TIMEZONE, timeZoneName: 'short',
  }).formatToParts(dt).find((p) => p.type === 'timeZoneName')?.value || 'PT';
  return `${formatted} ${abbr}`;
};

/**
 * Split a money value into the parts the Future Form figures set separately:
 * "$69,708" large, ".38" raised beside it.
 *
 * Returns { dollars, cents } as strings — "$69,708" and ".38" — so a caller
 * can render <span>{dollars}<sup>{cents}</sup></span>. Rounds like
 * formatCurrency does, so the two never disagree by a cent.
 */
export const splitCurrency = (amount) => {
  const text = formatCurrency(amount || 0);
  const dot = text.lastIndexOf('.');
  if (dot === -1) return { dollars: text, cents: '' };
  return { dollars: text.slice(0, dot), cents: text.slice(dot) };
};

/** Whole dollars, no cents. For summary figures that do not need them. */
export const formatCurrencyShort = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(amount || 0);

/**
 * An activity timestamp as the two lines the table shows: the day on top,
 * the clock time beneath.
 *
 * Both are rendered in the FIRM's timezone, not the viewer's — the same
 * reason formatDateTime does, and the reason "Today" has to be computed
 * there too. A 9pm Pacific event read on an east-coast laptop is still
 * today's work, and a viewer in another zone must not see a different day
 * from the person sitting next to them.
 */
export const formatWhen = (value) => {
  const dt = parseServerDate(value);
  if (!dt) return { day: '', time: '' };

  const dayKey = (d) => d.toLocaleDateString('en-CA', { timeZone: FIRM_TIMEZONE });
  const now = new Date();
  const yesterday = new Date(now.getTime() - 86400000);

  let day;
  if (dayKey(dt) === dayKey(now)) day = 'Today';
  else if (dayKey(dt) === dayKey(yesterday)) day = 'Yesterday';
  else day = dt.toLocaleDateString('en-US', { timeZone: FIRM_TIMEZONE, month: 'short', day: 'numeric' });

  const time = dt.toLocaleTimeString('en-US', {
    timeZone: FIRM_TIMEZONE, hour: 'numeric', minute: '2-digit',
  });
  return { day, time };
};
